import withProviderTracking from "@indeed/jobsearch-react-native-bundle/shared/components/ProviderRoot/ProviderRoot";
import type { LocaleData } from "@indeed/jobsearch-react-native-bundle/shared/I18n";
import { setDatadogLoggerImplementation } from "@indeed/jobsearch-react-native-bundle/shared/logging/DatadogLogger.web";
import { enrichContext } from "@indeed/jobsearch-react-native-bundle/shared/logging/DatadogLoggerContext";
import { NavigationType } from "@indeed/jobsearch-react-native-bundle/shared/util/NavigationType";
import AppWithSuspenseBoundary, {
  AppProps,
  loadAppContentComponent,
} from "@indeed/jobsearch-react-native-bundle/viewjob/src/App";
import { shouldUseOneGraphDataPath } from "@indeed/jobsearch-react-native-bundle/viewjob/src/lib/shouldUseOneGraphDataPath";
import { AppRegistry } from "react-native";

import { createAppWithSafeArea } from "./safeArea";
import { initializeViewjobDatadog } from "./src/datadog/datadogConfig";

// Wire the web Datadog logger so DatadogLogger (e.g. useLogErrorOnce) emits on
// web instead of no-op'ing (RN-5721). The strict-entry codegen rewrites the
// `shared/` package paths above to `shared-strict/`, so each bundle registers
// on its own DatadogLogger.web instance. No-op until the host injects
// globalThis.rnViewjobDatadogConfig.
initializeViewjobDatadog({ setDatadogLoggerImplementation, enrichContext });

// SafeAreaProvider must wrap the app on web standalone so that SafeAreaProviderCompat
// inside NativeStackView (react-native-screens) finds existing insets context and renders
// its container with flex:1. Without it, NativeSafeAreaProvider.web renders a View with
// no style, collapsing the height chain and preventing StickyButtonsRoot's ScrollView
// from getting a bounded height — nothing can scroll. The SafeArea wiring lives in the
// shared `./safeArea` helper (see index.library.tsx / index.server.ts).
const AppWithSafeArea = createAppWithSafeArea<AppProps>(AppWithSuspenseBoundary);

AppRegistry.registerComponent("main", () => withProviderTracking(AppWithSafeArea, "ViewJob"));

const rootTag = document.getElementById("root");
if (process.env.NODE_ENV !== "production") {
  if (!rootTag) {
    throw new Error('Required HTML element with id "root" was not found in the document HTML.');
  }
}

const hydrate = globalThis._initialData?.ssr ?? false;

const initialProps: AppProps = {
  ...globalThis._rootProps,
  navigationType: NavigationType.PUSH_AS_FIRST,
  preloadedVJData:
    globalThis._rootProps?.preloadedVJData ?? globalThis._initialData?.viewJobClientSideModel,
  // Active-locale gettext catalog inlined by the host (jobseeker-frontend)
  // into _initialData; the client bundle no longer ships the all-locales
  // catalog (RN-6851 / DISCO-5470). Falls back to the bundled catalog when absent.
  catalog: (globalThis._initialData as unknown as { i18nCatalog?: LocaleData } | undefined)
    ?.i18nCatalog,
};

function start(preloadedContent: AppProps = {}) {
  try {
    AppRegistry.runApplication("main", {
      rootTag,
      hydrate,
      initialProps: { ...initialProps, ...preloadedContent } satisfies AppProps,
    });
  } catch (e: unknown) {
    // @ts-ignore
    globalThis._latestError = e;
  }
}

if (!hydrate) {
  start();
} else {
  // React discards the server HTML of any Suspense boundary that suspends while
  // hydrating, so reaching App's lazy content component with its chunk still
  // unfetched blanks the SSR'd job for a chunk round trip. Resolve that chunk
  // first and hand the component in, so hydration runs synchronously against
  // the markup already on screen. The data path must match App's own choice
  // (shouldUseOneGraphDataPath) or the preloaded component is ignored and the
  // blink returns.
  const useOneGraph = shouldUseOneGraphDataPath({
    platformOS: "web",
    forceOneGraph: initialProps.forceOneGraph,
    isApolloDriven: globalThis.__APOLLO_STATE__ != null,
    // Web never reads the native rn_viewjob_onegraph_tst allocation.
    oneGraphAllocationValue: 0,
  });

  loadAppContentComponent(useOneGraph).then(
    (component) =>
      start(useOneGraph ? { oneGraphComponent: component } : { restComponent: component }),
    // A failed chunk fetch still boots the app on the lazy path: the SSR markup
    // blinks, but the page works.
    () => start(),
  );
}
